// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { createHorizontalSmallHeadingAndTextStyles } from "@synd/App/SharedStyles/smallHeadingAndTextStyles";
import { CustomAppState } from "@synd/App/Store/Reducers";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { xScreen } from "@synd/XDL/Utilities";
import React, { useState } from "react";
import { connect, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: InsufficientInventoryModalStyles;
}

const mapStateToProps = (state: CustomAppState) => ({
    modalIsOpen: state.xCommon.isInsufficientInventoryModal?.isOpen,
    productInfo: state.xCommon.isInsufficientInventoryModal?.productInfo,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface InsufficientInventoryModalStyles {
    modal?: ModalPresentationProps;
    messageText?: TypographyPresentationProps;
    information?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    productTitle?: TypographyPresentationProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
}

export const insufficientInventoryModalStyles: InsufficientInventoryModalStyles = {
    modal: {
        sizeVariant: "small",
    },
    submitButton: {
        variant: "primary",
        css: css`
            margin-left: auto;
        `,
    },
    messageText: {
        variant: "p",
        ...fontConfig.smallSingleLine,
        css: css`
            margin-bottom: 12px;
        `,
    },
    information: {
        css: css`
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
            margin-bottom: 16px;
            ${xScreen("lte", "sm")} {
                grid-template-column: 1fr;
            }
        `,
    },
    productTitle: {
        ...fontConfig.smallSingleLine,
    },
    headingAndTextStyles: {
        ...createHorizontalSmallHeadingAndTextStyles(),
    },
};

const InsufficientInventoryModal = ({ extendedStyles, modalIsOpen, productInfo }: Props) => {
    const [styles] = useState(() => mergeToNew(insufficientInventoryModalStyles, extendedStyles));
    const dispatch = useDispatch();

    const modalCloseHandler = () => {
        dispatch({
            type: "xCommon/update",
            data: {
                key: "isInsufficientInventoryModal",
                value: { isOpen: false, productInfo: {} },
            },
        } as any);
    };

    if (!productInfo) {
        return;
    }

    return (
        <Modal
            {...styles.modal}
            handleClose={modalCloseHandler}
            isOpen={modalIsOpen}
            headline={translate("Insufficient Stock")}
        >
            <Typography {...styles.messageText}>{siteMessage("Custom_Insufficient_Inventory_Modal")}</Typography>
            <StyledWrapper {...styles.information}>
                {productInfo.productNumber && (
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("Item #")}
                        text={productInfo.productNumber}
                    />
                )}
                {!!productInfo.qtyOrdered && (
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("Requested Quantity")}
                        text={productInfo.qtyOrdered}
                    />
                )}
                <Typography {...styles.productTitle}>{productInfo?.productTitle}</Typography>
                <SmallHeadingAndText
                    extendedStyles={styles.headingAndTextStyles}
                    heading={translate("Available Quantity")}
                    text={`${productInfo?.qtyOnHand || 0}`}
                />
            </StyledWrapper>
            <Button onClick={modalCloseHandler} {...styles.submitButton}>
                {translate("Okay")}
            </Button>
        </Modal>
    );
};

export default connect(mapStateToProps)(InsufficientInventoryModal);
